/**
 * Kanban Board Plugin - Main Entry Point
 * Registers Vuex store modules, global components, and plugin API
 */

import pluginMetadata from '../plugin.json';
import kanbanStore from './store/kanban';
import funnelStore from './store/funnel';
import AutomationActionFunnelInput from './components/widgets/AutomationActionFunnelInput.vue';
import KanbanActions from './routes/dashboard/conversation/KanbanActions.vue';
import { getOffersLabel } from './utils/pluginRuntimeConfig';

const automationContextKey = pluginMetadata.config.AUTOMATION_CONTEXT_KEY;
const automationContextLabel = pluginMetadata.config.AUTOMATION_CONTEXT_LABEL;
const automationContextIcon = pluginMetadata.config.AUTOMATION_CONTEXT_ICON;
const automationEventKeys = pluginMetadata.config.AUTOMATION_EVENT_KEYS || [];

const buildTextFilterOperators = () => [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
];

const kanbanAutomationExtension = {
  entityAttributes: {
    contact: {
      kanban_event_name: {
        name: 'KANBAN_EVENT_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_item_title: {
        name: 'KANBAN_ITEM_TITLE',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_funnel_name: {
        name: 'KANBAN_FUNNEL_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_stage_name: {
        name: 'KANBAN_STAGE_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_status: {
        name: 'KANBAN_STATUS',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_priority: {
        name: 'KANBAN_PRIORITY',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      kanban_conversation_display_id: {
        name: 'KANBAN_CONVERSATION_DISPLAY_ID',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
    },
  },
  eventContexts: {
    [automationContextKey]: {
      label: automationContextLabel,
      icon: automationContextIcon,
      events: automationEventKeys,
    },
  },
  eventEntities: automationEventKeys.reduce(
    (result, key) => ({
      ...result,
      [key]: ['contact'],
    }),
    {}
  ),
  actionsByEntity: {
    contact: [
      'delay_next_action',
      'delay_next_action_range',
      'send_webhook_event',
      'kanban_move_to_stage',
      'kanban_assign_agent',
      'kanban_create_note',
      'kanban_notify_team',
    ],
  },
  actionTypes: [
    {
      key: 'kanban_move_to_stage',
      label: 'KANBAN_MOVE_TO_STAGE',
      inputType: 'custom_component',
      component: 'KanbanAutomationActionFunnelInput',
    },
    {
      key: 'kanban_assign_agent',
      label: 'KANBAN_ASSIGN_AGENT',
      inputType: 'search_select',
      optionsSource: 'agents',
    },
    {
      key: 'kanban_create_note',
      label: 'KANBAN_CREATE_NOTE',
      inputType: 'textarea',
    },
    {
      key: 'kanban_notify_team',
      label: 'KANBAN_NOTIFY_TEAM',
      inputType: 'textarea',
    },
  ],
  automationRuleEvents: automationEventKeys.map(key => ({
    key,
    value: key.toUpperCase(),
    context: automationContextKey,
  })),
};

const mergeArrayRecords = (baseRecords = {}, extensionRecords = {}) => {
  const merged = { ...baseRecords };

  Object.entries(extensionRecords).forEach(([key, values]) => {
    merged[key] = Array.isArray(values)
      ? [...new Set([...(merged[key] || []), ...values])]
      : values;
  });

  return merged;
};

const mergeEntityAttributes = (
  baseAttributes = {},
  extensionAttributes = {}
) => {
  const merged = { ...baseAttributes };

  Object.entries(extensionAttributes).forEach(([entityKey, attributes]) => {
    merged[entityKey] = {
      ...(merged[entityKey] || {}),
      ...attributes,
    };
  });

  return merged;
};

const mergeEventContexts = (baseContexts = {}, extensionContexts = {}) => {
  const merged = { ...baseContexts };

  Object.entries(extensionContexts).forEach(([key, context]) => {
    merged[key] = {
      ...(merged[key] || {}),
      ...context,
      events: [
        ...new Set([...(merged[key]?.events || []), ...(context.events || [])]),
      ],
    };
  });

  return merged;
};

const mergeAutomationRuleEvents = (baseEvents = [], extensionEvents = []) => {
  const seen = new Map();

  [...baseEvents, ...extensionEvents].forEach(event => {
    if (event?.key) {
      seen.set(event.key, event);
    }
  });

  return Array.from(seen.values());
};

const getSidebarLocale = () => {
  if (
    typeof document !== 'undefined' &&
    typeof document.documentElement?.lang === 'string' &&
    document.documentElement.lang.trim().length > 0
  ) {
    return document.documentElement.lang;
  }

  if (typeof navigator !== 'undefined') {
    return navigator.language || navigator.languages?.[0] || 'en';
  }

  return 'en';
};

const getSidebarText = (payload, key, fallback, params = undefined) => {
  try {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const translated = payload?.t?.(key, params);
    if (translated && translated !== key) {
      return translated;
    }
  } catch {
    // Ignore translation failures and keep the fallback.
  }

  return fallback;
};

const getStoreGetter = (store, getterKey, fallback) => {
  if (!store?.getters || !(getterKey in store.getters)) {
    return fallback;
  }

  return store.getters[getterKey];
};

const isAuthLoading = store =>
  Boolean(getStoreGetter(store, 'getAuthUIFlags', {})?.isFetching);

const hasAuthenticatedAccountContext = store =>
  !isAuthLoading(store) &&
  Boolean(getStoreGetter(store, 'isLoggedIn', false)) &&
  Boolean(getStoreGetter(store, 'getCurrentAccountId', null));

const waitForAuthenticatedAccountContext = store => {
  if (!store?.subscribe) {
    return Promise.resolve(hasAuthenticatedAccountContext(store));
  }

  if (hasAuthenticatedAccountContext(store)) {
    return Promise.resolve(true);
  }

  if (!isAuthLoading(store)) {
    return Promise.resolve(false);
  }

  return new Promise(resolve => {
    const unsubscribe = store.subscribe(() => {
      if (hasAuthenticatedAccountContext(store)) {
        unsubscribe();
        resolve(true);
        return;
      }

      if (!isAuthLoading(store)) {
        unsubscribe();
        resolve(false);
      }
    });
  });
};

const sortItemsByRecentActivity = (leftItem, rightItem) => {
  const leftTimestamp = new Date(
    leftItem?.updated_at || leftItem?.created_at || 0
  ).getTime();
  const rightTimestamp = new Date(
    rightItem?.updated_at || rightItem?.created_at || 0
  ).getTime();

  return rightTimestamp - leftTimestamp;
};

const buildRecentKanbanSidebarItems = (payload, store) => {
  const selectedFunnel = getStoreGetter(
    store,
    'funnel/getSelectedFunnel',
    null
  );
  const kanbanItems = getStoreGetter(store, 'kanban/getKanbanItems', []);

  if (!selectedFunnel?.id || !Array.isArray(kanbanItems)) {
    return [];
  }

  return kanbanItems
    .filter(item => {
      const itemFunnelId = item?.funnel_id || item?.funnel?.id || null;
      return String(itemFunnelId) === String(selectedFunnel.id);
    })
    .sort(sortItemsByRecentActivity)
    .slice(0, 5)
    .map(item => ({
      name: `item-${item.id}`,
      label: item?.item_details?.title?.trim() || `Item ${item.id}`,
      activeOn: ['kanban_board'],
      to: payload.accountScopedRoute('kanban_board', {}, { item: item.id }),
    }));
};

const buildKanbanSidebarChildren = (payload, store) => {
  const locale = getSidebarLocale();
  const dashboardViewEnabled = getStoreGetter(
    store,
    'kanban/getDashboardViewEnabled',
    true
  );
  const funnels = getStoreGetter(store, 'funnel/getFunnels', []) || [];
  const kanbanTitle =
    getStoreGetter(store, 'kanban/getKanbanTitle', 'Kanban') || 'Kanban';
  const visibleFunnels = funnels.filter(funnel => funnel?.active !== false);
  const offersLabel =
    getOffersLabel(locale) ||
    getSidebarText(payload, 'KANBAN.TABS.OFFERS', 'Offers');

  const children = [];

  if (dashboardViewEnabled) {
    children.push({
      name: 'Kanban Dashboard',
      label: getSidebarText(payload, 'SIDEBAR.KANBAN_DASHBOARD', 'Dashboard'),
      icon: 'i-lucide-layout-dashboard',
      activeOn: ['kanban_board'],
      to: payload.accountScopedRoute('kanban_board', {}, { view: 'dashboard' }),
    });
  }

  children.push(
    {
      name: 'Kanban Stages',
      label: getSidebarText(payload, 'SIDEBAR.KANBAN_STAGES', 'Stages'),
      icon: 'i-lucide-kanban',
      activeOn: ['kanban_board'],
      to: payload.accountScopedRoute('kanban_board'),
    },
    {
      name: 'Kanban Offers',
      label: getSidebarText(
        payload,
        'SIDEBAR.KANBAN_OFFERS_TITLE',
        offersLabel,
        { title: offersLabel }
      ),
      icon: 'i-lucide-package-search',
      activeOn: ['kanban_board'],
      to: payload.accountScopedRoute('kanban_board', {}, { view: 'offers' }),
    },
    {
      name: 'Kanban Templates',
      label: getSidebarText(
        payload,
        'SIDEBAR.KANBAN_MESSAGE_TEMPLATES',
        'Message Templates'
      ),
      icon: 'i-lucide-message-circle-more',
      activeOn: ['kanban_board'],
      to: payload.accountScopedRoute('kanban_board', {}, { view: 'templates' }),
    }
  );

  if (visibleFunnels.length > 0) {
    children.push({
      name: 'Kanban Funnels',
      label: getSidebarText(payload, 'SIDEBAR.KANBAN_FUNNELS', 'Funnels'),
      icon: 'i-lucide-workflow',
      activeOn: ['kanban_board'],
      children: visibleFunnels.map(funnel => ({
        name: `funnel-${funnel.id}`,
        label: funnel.name,
        activeOn: ['kanban_board'],
        to: payload.accountScopedRoute(
          'kanban_board',
          {},
          { funnel: funnel.id }
        ),
      })),
    });
  }

  const recentItems = buildRecentKanbanSidebarItems(payload, store);
  if (recentItems.length > 0) {
    children.push({
      name: 'Kanban Recent Items',
      label: getSidebarText(
        payload,
        'SIDEBAR.KANBAN_RECENT_ITEMS',
        'Recent Items'
      ),
      icon: 'i-lucide-clock',
      activeOn: ['kanban_board'],
      children: recentItems,
    });
  }

  return {
    name: 'Kanban',
    label: getSidebarText(payload, 'SIDEBAR.KANBAN_BOARD_TITLE', kanbanTitle, {
      title: kanbanTitle,
    }),
    icon: 'i-lucide-layout-grid',
    to: payload.accountScopedRoute('kanban_board'),
    activeOn: ['kanban_board'],
    children,
  };
};

const refreshSidebar = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
  }
};

const subscribeKanbanSidebarRefresh = store => {
  if (!store?.subscribe) {
    return;
  }

  const sidebarRelevantMutations = new Set([
    'funnel/SET_FUNNELS',
    'funnel/SET_SELECTED_FUNNEL',
    'kanban/SET_KANBAN_ITEMS',
    'kanban/APPEND_ITEMS',
    'kanban/ADD_KANBAN_ITEM',
    'kanban/REMOVE_KANBAN_ITEM',
    'kanban/UPDATE_ITEM_WITH_RESPONSE',
    'kanban/UPDATE_ITEM_DETAILS',
    'kanban/SET_KANBAN_CONFIG',
  ]);

  store.subscribe(mutation => {
    if (sidebarRelevantMutations.has(mutation.type)) {
      refreshSidebar();
    }
  });
};

const preloadKanbanSidebarData = async store => {
  if (!store?.dispatch) {
    refreshSidebar();
    return;
  }

  try {
    const canPreload = await waitForAuthenticatedAccountContext(store);
    if (!canPreload) {
      return;
    }

    await store.dispatch('funnel/fetch');

    const selectedFunnel = getStoreGetter(
      store,
      'funnel/getSelectedFunnel',
      null
    );

    if (selectedFunnel?.id) {
      await store.dispatch('kanban/fetchKanbanItems', { force: true });
    }
  } catch {
    // Ignore preload failures and let the sidebar render the static entries.
  } finally {
    refreshSidebar();
  }
};

export function install(app, { store, PluginSystem } = {}) {
  if (store) {
    if (!store.hasModule('kanban')) {
      store.registerModule('kanban', kanbanStore);
    }
    if (!store.hasModule('funnel')) {
      store.registerModule('funnel', funnelStore);
    }
  }

  app.component('KanbanActions', KanbanActions);
  app.component(
    'KanbanAutomationActionFunnelInput',
    AutomationActionFunnelInput
  );

  const pluginSystem =
    PluginSystem || window.chatwoot?.PluginSystem || window.PluginSystem;

  if (pluginSystem?.registerHook) {
    pluginSystem.registerHook('sidebar:menu-items', payload => {
      if (!payload?.items || !Array.isArray(payload.items)) {
        return payload;
      }

      const menuItem = buildKanbanSidebarChildren(payload, store);
      const existingIndex = payload.items.findIndex(
        item => item.name === 'Kanban'
      );

      if (existingIndex >= 0) {
        payload.items.splice(existingIndex, 1, menuItem);
        return payload;
      }

      const conversationIndex = payload.items.findIndex(
        item => item.name === 'Conversation'
      );
      const settingsIndex = payload.items.findIndex(
        item => item.name === 'Settings'
      );

      if (conversationIndex >= 0) {
        payload.items.splice(conversationIndex + 1, 0, menuItem);
      } else if (settingsIndex >= 0) {
        payload.items.splice(settingsIndex, 0, menuItem);
      } else {
        payload.items.push(menuItem);
      }

      return payload;
    });

    const {
      contact: automationContactAttributes = {},
      ...automationEntityAttributes
    } = kanbanAutomationExtension.entityAttributes || {};

    pluginSystem.registerHook('automation-enhanced:config', config => ({
      ...config,
      entityAttributes: mergeEntityAttributes(config.entityAttributes, {
        ...automationEntityAttributes,
        contact: {
          ...(config.entityAttributes?.contact || {}),
          ...automationContactAttributes,
        },
      }),
      eventContexts: mergeEventContexts(
        config.eventContexts,
        kanbanAutomationExtension.eventContexts
      ),
      eventEntities: mergeArrayRecords(
        config.eventEntities,
        kanbanAutomationExtension.eventEntities
      ),
      actionsByEntity: mergeArrayRecords(
        config.actionsByEntity,
        kanbanAutomationExtension.actionsByEntity
      ),
      actionTypes: mergeAutomationRuleEvents(
        config.actionTypes,
        kanbanAutomationExtension.actionTypes
      ),
      automationRuleEvents: mergeAutomationRuleEvents(
        config.automationRuleEvents,
        kanbanAutomationExtension.automationRuleEvents
      ),
    }));
  }

  if (typeof window !== 'undefined') {
    window.chatwoot = window.chatwoot || {};
    window.chatwoot.plugins = window.chatwoot.plugins || {};
    window.chatwoot.plugins['kanban-board'] = {
      version: pluginMetadata.version,
      config: pluginMetadata.config,
      store: store
        ? {
          kanban: () => store.state.kanban,
          funnel: () => store.state.funnel,
        }
        : null,
    };

    subscribeKanbanSidebarRefresh(store);
    preloadKanbanSidebarData(store);
  }
}

export default {
  install,
};
